import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["indicator"]
  static values = {
    chatId: Number,
    userId: Number
  }

  connect() {
    this.typingUsers = new Map()
    this.lastTypingSent = 0

    // If offline on connect, clear the indicator and don't set up cable
    if (localStorage.getItem("chat:online") === "false" || !navigator.onLine) {
      if (this.hasIndicatorTarget) this.indicatorTarget.classList.add("hidden")
      return
    }

    this.consumer = createConsumer()
    if (!this.consumer) return

    this.subscription = this.consumer.subscriptions.create(
      { channel: "ChatChannel", chat_id: this.chatIdValue },
      {
        received: (data) => this.handleReceived(data)
      }
    )
  }

  disconnect() {
    if (this.typingUsers) {
      this.typingUsers.forEach((timeout) => clearTimeout(timeout))
      this.typingUsers.clear()
    }

    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.consumer) {
      this.consumer.disconnect()
    }
  }

  notifyTyping() {
    // Don't broadcast typing when offline -- cable may not be connected
    if (localStorage.getItem("chat:online") === "false" || !navigator.onLine) return

    const now = Date.now()
    if (now - this.lastTypingSent < 2000) return

    this.lastTypingSent = now
    this.subscription.perform("typing")
  }

  handleReceived(data) {
    if (data.type !== "typing") return
    if (data.user_id === this.userIdValue) return

    // Clear existing timeout for this user
    if (this.typingUsers.has(data.user_id)) {
      clearTimeout(this.typingUsers.get(data.user_id))
    }

    // Set new timeout -- remove after 3 seconds of silence
    const timeout = setTimeout(() => {
      this.typingUsers.delete(data.user_id)
      this.renderIndicator()
    }, 3000)

    this.typingUsers.set(data.user_id, timeout)
    // Store the name alongside the timeout isn't possible with a simple Map,
    // so keep a separate names map
    if (!this.typingNames) this.typingNames = new Map()
    this.typingNames.set(data.user_id, data.user_name)

    this.renderIndicator()
  }

  renderIndicator() {
    if (!this.hasIndicatorTarget) return

    const names = []
    this.typingUsers.forEach((_timeout, userId) => {
      const name = this.typingNames?.get(userId)
      if (name) names.push(name)
    })

    if (names.length === 0) {
      this.indicatorTarget.classList.add("hidden")
      this.indicatorTarget.innerHTML = ""
      return
    }

    let text
    if (names.length === 1) {
      text = `${names[0]} is typing`
    } else if (names.length === 2) {
      text = `${names[0]} and ${names[1]} are typing`
    } else {
      text = `${names[0]} and ${names.length - 1} others are typing`
    }

    this.indicatorTarget.innerHTML = `
      <div class="flex items-center gap-1.5 text-xs text-gray-400">
        <span class="flex gap-0.5">
          <span class="typing-dot w-1 h-1 bg-gray-400 rounded-full"></span>
          <span class="typing-dot w-1 h-1 bg-gray-400 rounded-full" style="animation-delay: 0.15s"></span>
          <span class="typing-dot w-1 h-1 bg-gray-400 rounded-full" style="animation-delay: 0.3s"></span>
        </span>
        <span>${text}</span>
      </div>
    `
    this.indicatorTarget.classList.remove("hidden")
  }
}
